import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { cn } from '~/utils/cn';

interface ProductPagerProps {
  /** 1-based position across the whole filtered listing. */
  position: number;
  /** Total products matching the listing's filters/search. */
  total: number;
  /** Omit (or pass `undefined`) at the start of the list to disable the arrow. */
  onPrevious?: (() => void) | undefined;
  /** Omit at the end of the list to disable the arrow. */
  onNext?: (() => void) | undefined;
  /** True while the adjacent listing page is loading — both arrows wait on it. */
  isLoading?: boolean;
  /** `total` is only what's loaded so far (an infinite list) — shown as "7 of 50+". */
  hasMore?: boolean;
  className?: string;
}

/**
 * "‹ 7 of 128 ›" stepper for moving between sibling products without returning
 * to the listing. Position spans the entire filtered result set rather than the
 * current page, so the count matches what the listing's pagination reports.
 */
export function ProductPager({
  position,
  total,
  onPrevious,
  onNext,
  isLoading = false,
  hasMore = false,
  className,
}: ProductPagerProps) {
  const { t } = useTranslation('catalog-operations');
  const isPreviousDisabled = !onPrevious || isLoading;
  const isNextDisabled = !onNext || isLoading;

  return (
    <div
      data-testid="products-pager"
      className={cn('flex shrink-0 items-center gap-1.5', className)}
    >
      <Button
        data-testid="products-pager-previous-button"
        variant="outline"
        size="icon"
        onClick={onPrevious}
        disabled={isPreviousDisabled}
        aria-label={t('productPager.previous')}
        title={t('productPager.previous')}
        className={cn('size-8', isPreviousDisabled ? 'cursor-not-allowed' : 'cursor-pointer')}
      >
        <ChevronLeft className="size-4" />
      </Button>
      <span
        data-testid="products-pager-position"
        className="text-muted-foreground min-w-20 text-center text-sm tabular-nums"
      >
        {t(hasMore ? 'productPager.positionMore' : 'productPager.position', { position, total })}
      </span>
      <Button
        data-testid="products-pager-next-button"
        variant="outline"
        size="icon"
        onClick={onNext}
        disabled={isNextDisabled}
        aria-label={t('productPager.next')}
        title={t('productPager.next')}
        className={cn('size-8', isNextDisabled ? 'cursor-not-allowed' : 'cursor-pointer')}
      >
        <ChevronRight className="size-4" />
      </Button>
    </div>
  );
}
